import type { DrawerStep } from "./types";

type WindowWithIntercom = Window & { Intercom?: (command: string) => void };

export const DRAWER_SEEN_STAGE_KEY = "onboarding_drawer_seen_once";
export const DRAWER_SEEN_STAGE_NUMBER = 100;

export const DRAWER_STEPS: DrawerStep[] = [
  {
    key: "welcome",
    stageNumber: 7,
    sidebarTitle: "Signed up",
    sidebarIcon: "SignedUpIcon",
    estimatedTime: "5 minutes",
    content: {
      icon: "SignedUpIcon",
      accentColor: "#5ebb70",
      accentBgColor: "#1e3c23",
      hideTimeChip: true,
      title: "Welcome to DeskDay!",
      description:
        "Your account is set up and ready to go. You\u2019ve taken the first step toward transforming your MSP operations with AI-powered service delivery.",
      diamondBullets: [
        {
          text: "As a Super Admin, you have the highest level of access in DeskDay \u2014 full control over settings, users, billing, and every feature across your workspace.",
        },
        {
          text: "Bookmark your DeskDay portal for quick daily access",
          link: {
            label: "app.deskday.ai",
            url: "https://app.deskday.ai/",
          },
        },
        {
          text: "Join our Discord community to connect with fellow MSPs, share tips, and get the latest updates.",
          link: {
            label: "Join Discord",
            url: "https://discord.com/invite/dUGXkHuJ5n",
          },
        },
      ],
      primaryCta: {
        label: "Book a demo",
        route: "https://meetings.hubspot.com/jobin-johny/deskday-demo-meet",
      },
      secondaryCta: {
        label: "Chat with support",
        route: () => {
          const intercom = (window as WindowWithIntercom).Intercom;
          if (typeof intercom === "function") {
            intercom("show");
          }
        },
      },
      markCompleteVariant: "default",
    },
  },
  {
    key: "connect_support_mail",
    stageNumber: 9,
    sidebarTitle: "Connect your support email",
    sidebarIcon: "MailboxIcon",
    estimatedTime: "5 minutes",
    content: {
      icon: "MailboxIcon",
      accentColor: "#A094FF",
      accentBgColor: "#311679",
      title: "Connect your support email",
      description:
        "Connect your support mail to convert your emails into service desk tickets. You can add up to 5 support mailboxes and categorise them into different boards and queues.",
      whatYoullDo: [
        { text: "Sign in with your support mailbox credentials to connect." },
        { text: "Choose a Board and display name for the mailbox." },
        {
          text: "Set it as Primary so all outgoing emails are sent from your address.",
        },
      ],
      primaryCta: {
        label: "Connect support mail",
        route: "/app/control-center/channels/email?o=connect",
      },
      secondaryCta: {
        label: "Read guide",
        route: "https://support.deskday.com/en/collections/9556984-email",
      },
      proTip: {
        title: "Pro tip",
        body: "You can automatically sort incoming tickets using Workflows and route and update ticket properties based on condition rules you set:",
        bullets: [
          "Keywords in the email subject or message",
          "Who sent it (specific email address or domain)",
        ],
        cta: {
          label: "Explore service desk workflows",
          route: "/app/control-center/automation",
        },
      },
    },
  },
  {
    key: "team",
    stageNumber: 1,
    sidebarTitle: "Invite your team members",
    sidebarIcon: "TeamMembersIcon",
    estimatedTime: "10 minutes",
    content: {
      icon: "TeamMembersIcon",
      accentColor: "#6FAFE9",
      accentBgColor: "#0A3659",
      title: "Build your dream team",
      description:
        "Bring your team members and support staff onboard. Assign roles, set permissions, and get your entire team collaborating inside DeskDay.",
      whatYoullDo: [
        {
          text: "Add a team member, assign their role and work type (Remote or Onsite).",
        },
        {
          text: "Enable SSO for seamless login across your organisation.",
        },
        {
          text: "Configure notification preferences.",
          link: {
            label: "Open notifications",
            route: "/app/control-center/notifications",
          },
        },
      ],
      primaryCta: {
        label: "Add team member",
        route: "/app/control-center/team-members?o=add",
      },
      secondaryCta: {
        label: "Read guide",
        route: "https://support.deskday.com/en/collections/5702678-resources",
      },
      proTip: {
        title: "Pro tip",
        body: "Set up co-managed access for your customers by creating custom Roles and Permissions. Here\u2019s how it works:",
        bullets: [
          "Create a new role and select the specific customer it applies to",
          "Assign that role to the customer\u2019s team member",
          "They\u2019ll only see tickets belonging to their organisation \u2014 nothing else",
        ],
        cta: {
          label: "Add co-managed role",
          route: "/app/control-center/roles-and-permissions",
        },
      },
    },
  },
  {
    key: "branding",
    stageNumber: 8,
    sidebarTitle: "Configure branding",
    sidebarIcon: "BrandingIcon",
    estimatedTime: "10 minutes",
    content: {
      icon: "BrandingIcon",
      accentColor: "#e287d9",
      accentBgColor: "#63005f",
      title: "Make it yours",
      description:
        "Set up your brand identity in DeskDay so your team and customers experience a portal that looks and feels like yours.",
      whatYoullDo: [
        {
          text: "Set your brand name, upload your company logo and wordmark.",
        },
        {
          text: "Set up a custom domain for your Company portal (your internal team\u2019s access URL) and Customer portal (where customers submit tickets).",
        },
        {
          text: "Generate DNS records, add the CNAME entries to your domain registrar, then click Verify. DNS propagation can take 24\u201148 hours.",
        },
      ],
      primaryCta: {
        label: "Go to branding",
        route: "/app/control-center/branding",
      },
      secondaryCta: {
        label: "Read guide",
        route: "https://support.deskday.com/en/collections/5702681-branding",
      },
    },
  },
  {
    key: "customers",
    stageNumber: 4,
    sidebarTitle: "Onboard customers and users",
    sidebarIcon: "CustomersIcon",
    estimatedTime: "10 minutes",
    content: {
      icon: "CustomersIcon",
      accentColor: "#52bb9d",
      accentBgColor: "#184237",
      title: "Bring your customers onboard",
      description:
        "Add your customers and end users. Set up SLA, Billing Template, and contracts.",
      whatYoullDo: [
        { text: "Create a customer with their name and domain name." },
        {
          text: "Create users manually or sync them from Office 365 or Google.",
        },
        {
          text: "Configure customer-specific notification settings.",
          link: {
            label: "Open notifications",
            route: "/app/control-center/notifications",
          },
        },
      ],
      primaryCta: {
        label: "Create customer",
        route: "/app/customers/customers?o=create",
      },
      secondaryCta: {
        label: "Read guide",
        route:
          "https://support.deskday.com/en/collections/5702683-customers-and-end-users",
      },
      proTip: {
        title: "Pro tip",
        body: "If you use DeskDay to bill your customers, configure customer-specific billing settings under the Billing & Contract tab:",
        bullets: [
          "Billing Contacts \u2014 the recipients for all billing-related emails.",
          "Billing Address \u2014 the customer address displayed on invoices.",
          "Billing Contracts \u2014 create Fixed, Prepaid, Hourly, or Seat-based contracts and map them to tickets for automatic time entry calculation.",
        ],
      },
    },
  },
  {
    key: "channels",
    stageNumber: 6,
    sidebarTitle: "Explore Teams and mobile apps",
    sidebarIcon: "ChannelsIcon",
    estimatedTime: "5 minutes",
    content: {
      icon: "ChannelsIcon",
      accentColor: "#f5ab88",
      accentBgColor: "#54301e",
      title: "Stay connected everywhere",
      description:
        "DeskDay has dedicated apps for your technicians and end users \u2014 keeping everyone connected without relying on email or phone.",
      infoCards: [
        {
          title: "For technicians",
          body: "The DeskDay mobile app gives techs full ticket management on the go. Create, update, assign, and close tickets from iOS or Android.",
        },
        {
          title: "For end users",
          body: "Users can raise tickets, track status, and view previous requests through Microsoft Teams, the DeskDay Desktop app, or the Mobile app. No email or phone call needed.",
        },
      ],
      primaryCta: {
        label: "Download apps",
        route: "/app/home?o=download",
      },
      secondaryCta: {
        label: "Read guide",
        route:
          "https://support.deskday.com/en/articles/8235973-installing-it-connect-app",
      },
    },
  },
  {
    key: "integrations",
    stageNumber: 3,
    sidebarTitle: "Connect your RMM",
    sidebarIcon: "IntegrationsIcon",
    estimatedTime: "10 minutes",
    content: {
      icon: "IntegrationsIcon",
      accentColor: "#e4c468",
      accentBgColor: "#443a1e",
      title: "Supercharge with RMM integration",
      description:
        "Connect your remote monitoring & management (RMM) tool to DeskDay. Get device context on every ticket, automate alert-to-ticket conversion, and resolve issues faster.",
      whatYoullDo: [
        { text: "Integrate with NinjaOne, Level, Datto, or N-able N-central." },
        {
          text: "Auto-create tickets from RMM alerts with full device details.",
        },
        {
          text: "Take remote control of a user\u2019s device directly from the ticket screen.",
        },
      ],
      primaryCta: {
        label: "Connect your RMM",
        route: "/app/control-center/integrations",
      },
      secondaryCta: {
        label: "Read guide",
        route:
          "https://support.deskday.com/en/collections/9561825-integrations",
      },
    },
  },
];
